import { HttpHeaders, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { MfCaptchaService } from '@meddev/fe-shared/mf-captcha';
import { BaseServiceClient } from '@shared/data';
import { PaginatedRequest } from '@shared/data/model/paginatedRequest.model';
import { PaginatedResponse } from '@shared/data/model/paginatedResponse.model';
import { Observable } from 'rxjs';
import {
    BookingAllowanceInfoDto,
    BookingEligibilityDto,
    BsFreeSlotsRequestDto,
    ConfirmMcaTimeslotRequestDto,
    ContractorsPublicSearchResultDto,
    CreatePrereservationRequestDto,
    LocationsPublicSearchResultDto,
    MaskedTimeslotPlaceholdersResponseDto,
    PrereservationResponseDto,
    ServicePublicSearchResultDto,
    SimplePrereservationDto,
    SubcontractorsPublicSearchResultDto,
    TimeSlotDto,
} from '../dto';
import { BookingResponseDto } from '../dto/booking-response.dto';
import { BookingsResponseDto } from '../dto/bookings.dto';

export abstract class BookingServiceBase extends BaseServiceClient {
    readonly BookingV2BaseUrl = `${this.ApiBaseUrl}${this.appConstants.httpClient.bookingV2}`;
    readonly PatientApiBaseUrl = `${this.ApiBaseUrl}${this.appConstants.httpClient.patientApiBase}`;
    readonly McaBaseUrl = `${this.ApiBaseUrl}${this.appConstants.httpClient.mcaBase}`;
    private readonly mfCaptchaService = inject(MfCaptchaService);
    readonly AuthBypassOptions = {
        headers: new HttpHeaders({
            Authorization: this.appConstants.noAuthHeader,
        }),
    };

    getBookings(params: PaginatedRequest): Observable<PaginatedResponse<BookingsResponseDto>> {
        let httpParams = new HttpParams();
        httpParams = params.page ? httpParams.append('page', params.page.toString()) : httpParams;
        httpParams = params.pageSize ? httpParams.append('pageSize', params.pageSize.toString()) : httpParams;
        if (params.sort?.length) {
            params.sort.forEach(sort => {
                httpParams = httpParams.append('sort', `${sort.prop}:${sort.dir}`);
            });
        }
        return this.get(`${this.McaBaseUrl}/bookings`, { params: httpParams });
    }

    getBookingDetails(id: string, idType: 'bookingId' | 'prereservationId'): Observable<BookingsResponseDto> {
        if (idType !== 'bookingId' && idType !== 'prereservationId') {
            throw new Error(`Invalid idType`);
        }

        const params = new HttpParams().set(idType, id);
        return this.get(`${this.McaBaseUrl}/bookings/details`, { params });
    }

    cancelBooking(bookingId: string): Observable<void> {
        return this.delete(`${this.McaBaseUrl}/bookings/${bookingId}`);
    }

    getBookingDocumentContent(bookingId: string, documentId: string): Observable<Blob> {
        return this.get(`${this.McaBaseUrl}/bookings/${bookingId}/documents/${documentId}`, { responseType: 'blob' });
    }

    getBookingAllowanceInfo(allowanceToken: string): Observable<BookingAllowanceInfoDto> {
        return this.get<BookingAllowanceInfoDto>(`${this.PatientApiBaseUrl}/booking/info/${allowanceToken}`, this.AuthBypassOptions);
    }

    getBookingPlaceholders(): Observable<MaskedTimeslotPlaceholdersResponseDto> {
        return this.get(`${this.McaBaseUrl}/bookings/time-slots/placeholders`);
    }

    getContractors(contractorUuids: string[]): Observable<ContractorsPublicSearchResultDto> {
        let params = new HttpParams();
        params = params.append('uuids', contractorUuids?.join(','));
        return this.get(`${this.BookingV2BaseUrl}/public/contractors/`, { params });
    }

    getLocations(contractorUuids: string[]): Observable<LocationsPublicSearchResultDto> {
        let params = new HttpParams();
        params = params.append('contractorUuids', contractorUuids?.join(','));
        return this.get(`${this.BookingV2BaseUrl}/public/contractors/locations`, { params });
    }

    getServices(serviceUuids: string[]): Observable<ServicePublicSearchResultDto> {
        let params = new HttpParams();
        params = params.append('uuids', serviceUuids?.join(','));
        return this.get(`${this.BookingV2BaseUrl}/public/contractors/services`, { params });
    }

    getSubcontractors(contractorUuids: string[], bsId: number, bsServiceId: string): Observable<SubcontractorsPublicSearchResultDto> {
        let params = new HttpParams();
        params = bsId ? params.append('bsId', bsId) : params;
        params = contractorUuids?.length ? params.append('contractorUuids', contractorUuids?.join(',')) : params;
        params = bsServiceId ? params.append('bsServiceId', bsServiceId) : params;
        return this.get(`${this.BookingV2BaseUrl}/public/contractors/subcontractors/`, { params });
    }

    createPrereservation(data: CreatePrereservationRequestDto): Observable<PrereservationResponseDto> {
        const url = `${this.PatientApiBaseUrl}/prereservation`;
        return this.mfCaptchaService.post<PrereservationResponseDto>(this.getHttpClient(), 'prereservation', url, data, {
            ...this.AuthBypassOptions,
        });
    }

    confirmPrereservation(allowanceToken: string, data: SimplePrereservationDto): Observable<void> {
        let params = new HttpParams();
        params = allowanceToken ? params.append('token', allowanceToken) : params;
        return this.post(`${this.PatientApiBaseUrl}/vendor/reservation`, data, { params, ...this.AuthBypassOptions });
    }

    createBooking(createBookingRequest: ConfirmMcaTimeslotRequestDto): Observable<BookingResponseDto> {
        return this.post(`${this.McaBaseUrl}/bookings`, createBookingRequest);
    }

    getBookingEligibility(cusomerId: string, serviceExternalId: string): Observable<BookingEligibilityDto> {
        let params = new HttpParams();
        params = params.append('customerId', cusomerId);
        params = params.append('maskedServiceExternalId', serviceExternalId);
        return this.get(`${this.McaBaseUrl}/bookings/eligibility`, { params });
    }

    abstract getFreeSlots(bsId: number, allowanceToken: string, data: BsFreeSlotsRequestDto): Observable<TimeSlotDto[]>;
    abstract getFreeSlots(maskedBsId: string, maskedContractorId: string, data: BsFreeSlotsRequestDto): Observable<TimeSlotDto[]>;
}
